import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Clock, Newspaper } from 'lucide-react';
import FeaturedSection from '../components/news/FeaturedSection';
import ArticleCard from '../components/news/ArticleCard';
import PautaSlot from '../components/PautaSlot';
import { articleService, categoryService, editionService } from '../services/api';
import { useNewspaper } from '../context/AppContext';
import styles from './HomePage.module.css';

export default function HomePage() {
  const { config, categories, regionId, regionInfo } = useNewspaper();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [breakingArticles, setBreakingArticles] = useState([]);
  const [latestEdition, setLatestEdition] = useState(null);
  const [sectionArticles, setSectionArticles] = useState({});
  // Aviso para el lector cuando el sistema le sirvió la nacional
  // porque no había edición regional para su departamento.
  const [avisoFallback, setAvisoFallback] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Paso 1: pedir la edición vigente (regional con fallback a
      // nacional). El backend marca isFallbackNational=true si el
      // lector pidió regional y se le sirvió la nacional. Sobre esa
      // edición seleccionamos los artículos y el bloque destacado.
      const edition = await editionService.getLatest({ departmentId: regionId });
      setLatestEdition(edition);
      setAvisoFallback(!!edition?.isFallbackNational);

      // Paso 2: artículos VINCULADOS a esa edición (vía articulos_ediciones).
      // El mapper del service ya aplana cada artículo con los campos
      // de mapArticleList (title, image, category, author, etc.),
      // así que edition.articles es directamente una lista de artículos.
      //
      // Si un artículo está marcado como portada de la edición
      // (isCover=true), lo movemos al primer lugar para que
      // FeaturedSection lo use como hero grande. Solo hay uno por
      // edición (garantizado por el índice único parcial en BD).
      const raw = edition?.articles || [];
      const cover = raw.find(a => a.isCover);
      const articulosDeLaEdicion = cover
        ? [cover, ...raw.filter(a => !a.isCover)]
        : raw;
      setArticles(articulosDeLaEdicion);

      // Última hora: se sigue mostrando globalmente (no depende de la
      // edición). Es contenido de urgencia que aplica siempre.
      const { data: recentList } = await articleService.getAll({ limit: 30 });
      const breakingOnly = (recentList || []).filter(a => a.isBreaking);
      setBreakingArticles(breakingOnly);

      // Bloques por sección: se arman con los artículos de la edición,
      // agrupados por categoría. Si la edición tiene 20 artículos y 5
      // son de Economía, la sección Economía muestra esos 5.
      const sections = {};
      const mainCategories = categories.filter(c =>
        ['politica', 'economia', 'deportes', 'opinion', 'cultura'].includes(c.slug)
      );
      for (const category of mainCategories) {
        const suyos = articulosDeLaEdicion.filter(a =>
          a.category?.id === category.id
        ).slice(0, 4);
        if (suyos.length > 0) {
          sections[category.slug] = { category, articles: suyos };
        }
      }
      setSectionArticles(sections);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Ordenar artículos por vistas para trending
  const trendingArticles = [...articles]
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 5);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Cargando noticias...</p>
      </div>
    );
  }

  return (
    <main className={styles.home}>
      {/* Aviso: la home está mostrando la Nacional porque no hay edición
          regional para el departamento del lector. */}
      {avisoFallback && (
        <div style={{
          background: '#fff5e6',
          borderBottom: '1px solid #f5c78e',
          color: '#a15c00',
          padding: '10px 20px',
          textAlign: 'center',
          fontSize: 14,
        }}>
          Estás viendo la <strong>edición Nacional</strong>. Tu región
          {regionInfo?.departmentName ? ` (${regionInfo.departmentName})` : ''}
          {' '}aún no tiene una edición propia publicada.
        </div>
      )}

      {/* Breaking News / Última hora */}
      {breakingArticles.length > 0 && (
        <div className={styles.breakingNews}>
          <div className="container">
            <span className={styles.breakingLabel}>
              <Clock size={14} />
              Última hora
            </span>
            <div className={styles.breakingContent}>
              {breakingArticles.slice(0, 1).map(article => (
                <Link key={article.id} to={`/articulo/${article.slug}`}>
                  {article.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Banner publicitario superior */}
      <PautaSlot posicion="home_top" />

      {/* Sección principal destacada. Si la edición no tiene artículos
          vinculados aún, mostramos un mensaje editorial en vez de un
          FeaturedSection vacío que se ve raro. */}
      {articles.length > 0 ? (
        <FeaturedSection
          articles={articles.slice(0, 6)}
          trending={trendingArticles}
        />
      ) : (
        <div style={{
          maxWidth: 720, margin: '40px auto', padding: 40, textAlign: 'center',
          background: '#fafafa', borderRadius: 8,
        }}>
          <Newspaper size={40} style={{ color: '#999', marginBottom: 12 }} />
          <h2 style={{ marginTop: 0, fontFamily: 'var(--font-serif)' }}>
            {latestEdition
              ? 'Esta edición aún no tiene artículos'
              : 'No hay edición publicada para esta región'}
          </h2>
          <p style={{ color: '#666' }}>
            {latestEdition
              ? 'El equipo editorial está preparando el contenido. Vuelve en un momento.'
              : 'Prueba con otra región desde el selector superior.'}
          </p>
        </div>
      )}

      {/* Edición del día */}
      {latestEdition && (
        <section className={styles.editionBanner}>
          <div className="container">
            <div className={styles.editionContent}>
              <div className={styles.editionInfo}>
                <span className={styles.editionLabel}>
                  <Newspaper size={16} />
                  Edición del día
                </span>
                <h2 className={styles.editionTitle}>
                  Edición N° {latestEdition.number}
                </h2>
                <p className={styles.editionDate}>
                  {new Date(latestEdition.publishedAt).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                <Link to={`/ediciones/${latestEdition.number}`} className="btn btn-primary">
                  Ver edición completa
                </Link>
              </div>
              {latestEdition.coverImage && (
                <div className={styles.editionCover}>
                  <img src={latestEdition.coverImage} alt={`Edición ${latestEdition.number}`} />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Secciones por categoría */}
      {Object.entries(sectionArticles).map(([slug, { category, articles: catArticles }], sectionIdx) => (
        <>
          <section key={slug} className={styles.section}>
          <div className="container">
            <header className={styles.sectionHeader}>
              <h2 
                className={styles.sectionTitle}
                style={{ borderColor: category.color }}
              >
                <span style={{ backgroundColor: category.color }}></span>
                {category.name}
              </h2>
              <Link 
                to={`/seccion/${category.slug}`} 
                className={styles.sectionLink}
              >
                Ver todo
                <ChevronRight size={16} />
              </Link>
            </header>

            <div className={styles.sectionGrid}>
              {/* Artículo principal de la sección */}
              <div className={styles.sectionMain}>
                <ArticleCard 
                  article={catArticles[0]} 
                  variant="featured"
                  showExcerpt={true}
                />
              </div>

              {/* Artículos secundarios */}
              <div className={styles.sectionSecondary}>
                {catArticles.slice(1, 4).map(article => (
                  <ArticleCard 
                    key={article.id}
                    article={article}
                    variant="horizontal"
                    showExcerpt={false}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
          {/* Banner publicitario cada 2 secciones */}
          {sectionIdx % 2 === 1 && (
            <PautaSlot posicion="entre_articulos" />
          )}
        </>
      ))}

      {/* Opinión (sección especial) */}
      {sectionArticles.opinion && (
        <section className={styles.opinionSection}>
          <div className="container">
            <header className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <span style={{ backgroundColor: sectionArticles.opinion.category.color }}></span>
                Opinión
              </h2>
              <Link to="/seccion/opinion" className={styles.sectionLink}>
                Más columnas
                <ChevronRight size={16} />
              </Link>
            </header>

            <div className={styles.opinionGrid}>
              {sectionArticles.opinion.articles.map(article => (
                <article key={article.id} className={styles.opinionCard}>
                  <div className={styles.opinionAuthor}>
                    {article.author?.avatar && (
                      <img 
                        src={article.author.avatar} 
                        alt={article.author.name}
                        className={styles.opinionAvatar}
                      />
                    )}
                    <div>
                      <span className={styles.opinionAuthorName}>
                        {article.author?.name}
                      </span>
                      <span className={styles.opinionAuthorRole}>
                        {article.author?.role}
                      </span>
                    </div>
                  </div>
                  <h3 className={styles.opinionTitle}>
                    <Link to={`/articulo/${article.slug}`}>
                      {article.title}
                    </Link>
                  </h3>
                  <p className={styles.opinionExcerpt}>
                    {article.excerpt}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Todas las categorías */}
      <section className={styles.allSections}>
        <div className="container">
          <h2 className={styles.allSectionsTitle}>Explora todas las secciones</h2>
          <div className={styles.categoriesGrid}>
            {categories.filter(c => c.slug !== 'portada').map(category => (
              <Link 
                key={category.id}
                to={`/seccion/${category.slug}`}
                className={styles.categoryCard}
                style={{ '--category-color': category.color }}
              >
                <span className={styles.categoryIcon}>{category.icon}</span>
                <span className={styles.categoryName}>{category.name}</span>
                <ChevronRight size={16} className={styles.categoryArrow} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
